// Mantis #1453 — M001-D029 §5.2/§6 (F0306/F0308/F0310/F0311), contract
// SAPI-D029-004: the membership editor for one published group of this
// organisation's ONE bound realm (cardinality 1:1, lead ruling — no
// installation scope anywhere).
//
// Interaction shape reused from the access-schedule Groups screen (F0307):
// person picker, per-member toggle, a single Save, StepUpDialog on a 401
// challenge — with the access-schedule discipline amended exactly as D029
// requires:
// - the picker is fed ONLY by RealmAuthority's published directory (decision R);
// - Save submits the WHOLE staged batch as ONE submitGroupMembershipChanges
//   request ({changes:[{objectSid,op}]}, max 50) — the contract has no
//   client-supplied idempotency key and returns one resolved
//   MembershipChangeOutcome per change, synchronously (§6);
// - a per-change refusal (NOT_OPTED_IN/NOT_IN_DIRECTORY/NOT_EXPORTABLE) is
//   shown against that member and never invalidates sibling changes (F0310);
// - DOOR_WRITE_BLOCKED is a CONSEQUENCE of an applied change, never a
//   refusal (lead ruling 3) — rendered by RealmAuthorityMembershipOutcomes;
// - RA_OFFLINE refuses the WHOLE batch before any command is constructed:
//   this editor also refuses client-side before any call when the link is
//   not demonstrably up, with the mandatory decision-G wording and the last
//   snapshot time — nothing is queued (§7);
// - no per-removal confirmation (owner ruling 2);
// - after any resolved batch, the caller's onReload() re-fetches the real
//   groups/directory views — this component never fabricates an updated
//   mirror from what the browser asked for; the contract's batch response
//   carries outcomes, not a group snapshot;
// - M004-F0628: a failed save NEVER discards the staged draft. Only the
//   entries RealmAuthority actually answered (outcome applied or refused)
//   leave the draft; entries with no outcome — a 5xx, a network error, or
//   the tail of a batch a mid-way RA_OFFLINE 409 never reached — stay
//   staged so the operator retries without re-entering them. A 409 that
//   carries partial `results` (Core lane, concurrent) clears exactly those;
// - M004-F0629: the group's `pending[]` commands (dispatched, not yet
//   acknowledged) are rendered so the operator can see what is queued, and
//   a person with a queued command cannot be staged again (F0309: one
//   command in flight per (group, person));
// - M004-F0630: a timed-out command is reported as "may still have been
//   applied — refresh", never as RealmAuthority having gone away;
// - M004-F0670 (WebUI #140): a 200 whose body does not project is an
//   UNKNOWN result, never "not accepted": Core dispatched every change and
//   RealmAuthority answered, so the change may well have applied. The
//   editor says the result could not be confirmed, re-reads the real mirror
//   (onReload) and keeps the draft for F0628's entry-by-entry
//   reconciliation; it renders no outcome rows and clears nothing on the
//   strength of the unverified body. Validation itself is unchanged;
// - M004-F0698/F0699: the applied rows' commandIds are polled (bounded
//   backoff, visible page only) for RealmAuthority's settled door outcome and
//   a row is upgraded only by an outcome for its own commandId;
// - M004-F0671: that notice carries a value-free diagnostic (first rejected
//   field, body type, media type, build id) for the next occurrence.
import React, { useEffect, useMemo, useState } from 'react';
import DirectoryPicker from './DirectoryPicker';
import RealmAuthorityRefusalNotice, { RA_OFFLINE_TEXT, UNVERIFIED_RESULT_TEXT } from './RealmAuthorityRefusalNotice';
import RealmAuthorityMembershipOutcomes from './RealmAuthorityMembershipOutcomes';
import PersonDrawer from './PersonDrawer';
import useDoorOutcomePoll from './useDoorOutcomePoll';
import { getStepUpChallengeFromError } from '../../../utils/stepUp';
import { realmAuthorityGroupsApi } from '../../../services/realmAuthorityGroupsApi';
import {
  describeUnverifiedResponse, directoryBySid, enrichMembers, formatDateTime, projectMembershipBatch,
  projectPartialResults, pruneDraft, resolvedSids,
} from './normalisers';
import './realmAuthority.css';

export default function RealmAuthorityGroupEditor({
  organisationId, link, group, directory, groups, performWrite, onReload, doorPollSchedule,
}) {
  const directoryMap = useMemo(() => directoryBySid(directory), [directory]);
  const [pickerSid, setPickerSid] = useState('');
  const [stagedAdds, setStagedAdds] = useState([]);
  const [stagedRemovals, setStagedRemovals] = useState([]);
  const [pendingSids, setPendingSids] = useState([]);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState('');
  const [notice, setNotice] = useState(null);
  const [outcomes, setOutcomes] = useState([]);
  // MAD001-F0236: the person whose NAME was clicked in the member table —
  // the side drawer's subject. Opening/closing it never touches the staged
  // draft below.
  const [drawerPerson, setDrawerPerson] = useState(null);

  // M004-F0698: the commands this page just sent whose ack said "door state
  // not settled" are polled for RA's door outcome, by exact commandId.
  const trackedDoorCommands = useMemo(
    () => outcomes.filter((entry) => entry.outcome === 'applied' && entry.commandId && entry.doorWriteStatus === null),
    [outcomes],
  );
  const doorPoll = useDoorOutcomePoll({
    organisationId,
    groupSid: group?.groupSid,
    tracked: trackedDoorCommands,
    schedule: doorPollSchedule,
  });

  const linkUp = link?.linked === true && link?.tunnelConnected === true;
  const mirrorSids = useMemo(() => new Set(group?.members?.map((member) => member.objectSid) || []), [group]);
  const members = useMemo(() => enrichMembers(group, directoryMap), [group, directoryMap]);
  const queued = useMemo(() => group?.pending || [], [group]);
  const queuedBySid = useMemo(() => {
    const map = new Map();
    for (const command of queued) if (!map.has(command.objectSid)) map.set(command.objectSid, command);
    return map;
  }, [queued]);

  // M004-F0628: after every real re-fetch, reconcile the kept draft against
  // the mirror — a staged add whose person is now a member (including a
  // timed-out command that did apply after all) is done; a staged removal
  // whose person is gone is done. Nothing else is touched.
  useEffect(() => {
    setStagedAdds((current) => pruneDraft({ stagedAdds: current }, mirrorSids).stagedAdds);
    setStagedRemovals((current) => pruneDraft({ stagedRemovals: current }, mirrorSids).stagedRemovals);
  }, [mirrorSids]);

  const checked = (objectSid) => (
    mirrorSids.has(objectSid) ? !stagedRemovals.includes(objectSid) : stagedAdds.includes(objectSid)
  );

  const toggle = (objectSid) => {
    setNotice(null);
    setOutcomes([]);
    setInfo('');
    if (mirrorSids.has(objectSid)) {
      setStagedRemovals((current) => (current.includes(objectSid)
        ? current.filter((sid) => sid !== objectSid)
        : [...current, objectSid]));
    } else {
      setStagedAdds((current) => (current.includes(objectSid)
        ? current.filter((sid) => sid !== objectSid)
        : [...current, objectSid]));
    }
  };

  const stageFromPicker = () => {
    if (!pickerSid || mirrorSids.has(pickerSid) || stagedAdds.includes(pickerSid) || queuedBySid.has(pickerSid)) return;
    setNotice(null);
    setStagedAdds((current) => [...current, pickerSid]);
    setPickerSid('');
  };

  // Drop ONLY the entries a set of outcomes answered (applied|refused).
  const clearResolved = (results) => {
    const done = resolvedSids(results);
    if (!done.size) return;
    setStagedAdds((current) => current.filter((sid) => !done.has(sid)));
    setStagedRemovals((current) => current.filter((sid) => !done.has(sid)));
  };

  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    setInfo('');
    setNotice(null);
    setOutcomes([]);

    const additions = stagedAdds.filter((sid) => !mirrorSids.has(sid) && !queuedBySid.has(sid));
    const removals = stagedRemovals.filter((sid) => mirrorSids.has(sid) && !queuedBySid.has(sid));
    if (!additions.length && !removals.length) {
      setInfo('No membership changes to save.');
      return;
    }

    // §7: no live tunnel → refuse before any command is constructed. No
    // queue — the staged draft is kept so the admin can retry once the link
    // is back, never silently discarded.
    if (!linkUp) {
      setNotice({
        code: 'RA_OFFLINE',
        message: RA_OFFLINE_TEXT,
        lastSnapshotAt: link?.lastSnapshotAt || group?.publishedAt || '',
      });
      return;
    }

    const changes = [
      ...additions.map((objectSid) => ({ objectSid, op: 'add' })),
      ...removals.map((objectSid) => ({ objectSid, op: 'remove' })),
    ];
    setSaving(true);
    setPendingSids(changes.map((entry) => entry.objectSid));
    try {
      await performWrite(async () => {
        const response = await realmAuthorityGroupsApi.submitMembershipChanges(
          organisationId,
          group.groupSid,
          changes,
        );
        const batch = projectMembershipBatch(response);
        if (!batch) {
          setNotice({
            code: 'UNVERIFIED_RESULT',
            message: UNVERIFIED_RESULT_TEXT,
            diagnostic: describeUnverifiedResponse(response, document.documentElement?.dataset?.buildId),
            retained: true,
          });
          onReload?.();
          return;
        }
        setOutcomes(batch.results);
        clearResolved(batch.results);
        // The batch response carries outcomes, never a group snapshot — the
        // only honest mirror update is a real re-fetch (§6.1 amended).
        onReload?.();
      });
    } catch (writeError) {
      if (getStepUpChallengeFromError(writeError)?.id) throw writeError;
      const status = writeError?.response?.status;
      const code = writeError?.response?.data?.error;
      if (status === 409 && code === 'RA_OFFLINE') {
        // M004-F0628: RealmAuthority may have gone offline PART-WAY through
        // the batch. Whatever Core reports as already answered leaves the
        // draft; everything else stays staged for the retry. No results
        // (today's shape) or an unverifiable results array → keep it all.
        const partial = projectPartialResults(writeError.response.data);
        if (partial?.length) {
          setOutcomes(partial);
          clearResolved(partial);
          onReload?.();
        }
        setNotice({
          code: 'RA_OFFLINE',
          message: writeError.response.data.message || RA_OFFLINE_TEXT,
          lastSnapshotAt: writeError.response.data.details?.lastSnapshotAt || '',
          retained: true,
        });
      } else if (status === 404 && (code === 'NOT_LINKED' || code === 'GROUP_NOT_MIRRORED')) {
        setNotice({ code, message: writeError.response.data.message || '' });
      } else {
        setNotice({
          code: 'ERROR',
          message: writeError?.response?.data?.message || writeError?.message || '',
          retained: true,
        });
      }
    } finally {
      setSaving(false);
      setPendingSids([]);
      // Deliberately NO draft reset here (M004-F0628): the draft is cleared
      // only entry-by-entry from real outcomes, above.
    }
  };

  const draftCount = stagedAdds.filter((sid) => !mirrorSids.has(sid)).length
    + stagedRemovals.filter((sid) => mirrorSids.has(sid)).length;

  const editorDisabled = !group || saving || !(directory?.length > 0);
  const pickerAvailable = linkUp && directory?.length > 0;

  return (
    <>
      <form className="ra-editor" onSubmit={save} data-testid={`ra-editor-${group?.groupSid || 'none'}`}>
      <h4 className="ra-editor-title">Members — {group?.groupName}</h4>
      {!linkUp ? (
        <p className="ra-editor-offline" role="status" data-testid="ra-editor-offline">
          {RA_OFFLINE_TEXT}
          {link?.lastSnapshotAt ? ` Last snapshot: ${formatDateTime(link.lastSnapshotAt)}.` : ''}
          {' '}Membership changes need a live RealmAuthority connection. Nothing was sent.
          {draftCount ? ' Your staged changes are kept and Save re-enables when RealmAuthority reconnects.' : ''}
        </p>
      ) : null}
      {linkUp && !directory?.length ? (
        <p className="ra-editor-integrity" role="status" data-testid="ra-editor-no-directory">
          The people directory has not been published yet. Membership editing is
          not possible until RealmAuthority publishes it.
        </p>
      ) : null}

      <div className="ra-editor-picker">
        <DirectoryPicker
          directory={directory || []}
          value={pickerSid}
          onChange={setPickerSid}
          disabled={!pickerAvailable || editorDisabled}
        />
        <button
          type="button"
          className="btn btn-outline"
          onClick={stageFromPicker}
          disabled={!pickerAvailable || editorDisabled || !pickerSid}
        >
          Stage add
        </button>
      </div>

      {info ? <p className="ra-editor-notice" role="status">{info}</p> : null}
      <RealmAuthorityRefusalNotice notice={notice} onClose={() => setNotice(null)} />
      {notice?.retained && draftCount ? (
        <p className="ra-editor-notice" role="status" data-testid="ra-editor-draft-kept">
          {draftCount} staged change{draftCount === 1 ? '' : 's'} not yet applied — kept for you to retry.
        </p>
      ) : null}
      <RealmAuthorityMembershipOutcomes
        outcomes={outcomes}
        directoryMap={directoryMap}
        onRefresh={onReload}
        doorResults={doorPoll.results}
        doorPoll={doorPoll}
      />

      {queued.length ? (
        <section className="ra-pending" data-testid="ra-pending-commands" aria-label="Queued at RealmAuthority">
          <h5 className="ra-pending-title">Queued at RealmAuthority — not yet confirmed</h5>
          <ul className="ra-pending-list">
            {queued.map((command) => {
              const entry = directoryMap.get(command.objectSid);
              return (
                <li key={command.commandId} data-testid={`ra-pending-${command.objectSid}`}>
                  <strong>{entry?.displayName || entry?.upn || command.objectSid}</strong>
                  {' '}— {command.op === 'remove' ? 'removal' : 'addition'} sent
                  {command.dispatchedAt ? ` ${formatDateTime(command.dispatchedAt)}` : ''}, awaiting RealmAuthority's acknowledgement.
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <table className="ra-member-table">
        <thead>
          <tr><th scope="col">Member</th><th scope="col">In group</th></tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const pending = pendingSids.includes(member.objectSid);
            const deadRow = !member.inPublishedDirectory;
            const queuedCommand = queuedBySid.get(member.objectSid);
            return (
              <tr key={member.objectSid} data-testid={`ra-member-${member.objectSid}`} className={pending || queuedCommand ? 'ra-member--pending' : ''}>
                <td>
                  <button
                    type="button"
                    className="ra-member-name"
                    data-testid={`ra-member-name-${member.objectSid}`}
                    title="Open person details"
                    onClick={() => {
                      setNotice(null);
                      setOutcomes([]);
                      setInfo('');
                      setDrawerPerson(member);
                    }}
                  >
                    {member.displayName || member.upn}
                  </button>
                  <span className="ra-member-upn">{member.upn}</span>
                  {member.alsoCoreAccount ? (
                    <span className="ra-member-badge">also a Core account</span>
                  ) : null}
                  {deadRow ? (
                    <span className="ra-member-badge ra-member-badge--gone" data-testid="ra-member-not-in-directory">
                      no longer in the published directory
                    </span>
                  ) : null}
                  {queuedCommand ? (
                    <span className="ra-member-badge ra-member-badge--queued" data-testid={`ra-member-queued-${member.objectSid}`}>
                      {queuedCommand.op === 'remove' ? 'removal' : 'addition'} queued at RealmAuthority
                    </span>
                  ) : null}
                </td>
                <td>
                  <label>
                    <input
                      type="checkbox"
                      checked={checked(member.objectSid)}
                      disabled={editorDisabled || pending || deadRow || Boolean(queuedCommand)}
                      onChange={() => toggle(member.objectSid)}
                    />
                    {pending ? <span className="ra-member-pending">Sending…</span> : null}
                  </label>
                </td>
              </tr>
            );
          })}
          {stagedAdds.map((objectSid) => {
            const entry = directoryMap.get(objectSid);
            const pending = pendingSids.includes(objectSid);
            return (
              <tr key={`staged-${objectSid}`} data-testid={`ra-member-${objectSid}`} className="ra-member--staged">
                <td>
                  <strong>{entry?.displayName || entry?.upn || objectSid}</strong>
                  <span className="ra-member-upn">{entry?.upn || ''}</span>
                  {entry?.alsoCoreAccount ? <span className="ra-member-badge">also a Core account</span> : null}
                </td>
                <td>
                  <label>
                    <input
                      type="checkbox"
                      checked={!pending}
                      disabled={editorDisabled || pending}
                      onChange={() => setStagedAdds((current) => current.filter((sid) => sid !== objectSid))}
                    />
                    {pending ? <span className="ra-member-pending">Sending…</span> : null}
                  </label>
                </td>
              </tr>
            );
          })}
          {!members.length && !stagedAdds.length ? (
            <tr><td colSpan={2}>No members are in this group yet.</td></tr>
          ) : null}
        </tbody>
      </table>

      <button type="submit" className="btn btn-primary" disabled={editorDisabled || !linkUp}>
        {saving ? 'Saving…' : 'Save membership'}
      </button>
      <p className="ra-editor-help">
        One batch request is sent to RealmAuthority for every change staged
        here; a refusal of one member never blocks the others. Removals need
        no separate confirmation.
      </p>
      </form>

      {/* The drawer mounts OUTSIDE the editor form on purpose: its rename
          input owns its own form, and nested forms would make Enter trigger
          the editor's membership save (or a native navigation) instead of the
          drawer's commit. */}
      {drawerPerson ? (
        <PersonDrawer
          organisationId={organisationId}
          person={drawerPerson}
          groups={groups}
          performWrite={performWrite}
          onReload={onReload}
          onClose={() => setDrawerPerson(null)}
        />
      ) : null}
    </>
  );
}
